// ─── Roamer Vault™ — Storage Abstraction Layer ───────────────────────────────
// Sprint 112: provider-agnostic storage interfaces.
// Never hardcode a storage provider — swap implementations without changing callers.
//
// Supported providers (by priority):
//   1. Supabase Storage (current primary for cloud users)
//   2. Local IndexedDB cache (always available, used as write-through / offline)
//   3. Cloudflare R2 (future, for high-volume file storage)
//   4. Amazon S3 (future)

// ─── Core types ───────────────────────────────────────────────────────────────

export interface StorageObject {
  /** Storage key / path (e.g. "vault/user-123/file-abc.pdf") */
  key: string;
  /** MIME type */
  contentType: string;
  /** Size in bytes */
  sizeBytes: number;
  /** ISO timestamp */
  createdAt: string;
  /** ISO timestamp */
  updatedAt: string;
  /** Signed/public URL valid for `expiresInSeconds` */
  url: string;
  expiresInSeconds: number | null;
  /** Provider-specific metadata */
  metadata: Record<string, string>;
}

export interface StorageUploadOptions {
  /** MIME type override */
  contentType?: string;
  /** Arbitrary string key→value metadata */
  metadata?: Record<string, string>;
  /** Cache-Control header value */
  cacheControl?: string;
  /** Whether the object should be publicly readable */
  isPublic?: boolean;
  /** Progress callback (0–1) */
  onProgress?: (fraction: number) => void;
  /** AbortSignal for cancellation */
  signal?: AbortSignal;
}

export interface StorageListOptions {
  prefix?: string;
  limit?: number;
  cursor?: string;
}

export interface StorageListResult {
  objects: StorageObject[];
  nextCursor: string | null;
  hasMore: boolean;
}

// ─── Core storage interface ────────────────────────────────────────────────────

export interface VaultStorageProvider {
  readonly id: string;
  readonly name: string;
  readonly available: boolean;

  /**
   * Upload a file blob.
   * Returns the persisted StorageObject with a URL.
   */
  upload(key: string, blob: Blob, options?: StorageUploadOptions): Promise<StorageObject>;

  /**
   * Download a file by key.
   * Returns null if not found.
   */
  download(key: string): Promise<Blob | null>;

  /**
   * Get object metadata (cheap, no content transfer).
   */
  stat(key: string): Promise<StorageObject | null>;

  /**
   * Get a (possibly signed) URL for a key.
   * `expiresInSeconds` defaults to 3600 (1 hour) for signed URLs.
   */
  getUrl(key: string, expiresInSeconds?: number): Promise<string | null>;

  /**
   * Delete an object by key.
   */
  delete(key: string): Promise<void>;

  /**
   * List objects with optional prefix filter.
   */
  list(options?: StorageListOptions): Promise<StorageListResult>;

  /**
   * Move/rename an object.
   */
  move(fromKey: string, toKey: string): Promise<StorageObject>;
}

// ─── Storage manager (provider registry + routing) ────────────────────────────

class _VaultStorageManager {
  private providers = new Map<string, VaultStorageProvider>();
  private primaryId: string | null = null;

  register(provider: VaultStorageProvider, asPrimary = false): void {
    this.providers.set(provider.id, provider);
    if (asPrimary || !this.primaryId) {
      this.primaryId = provider.id;
    }
  }

  getPrimary(): VaultStorageProvider {
    const p = this.primaryId ? this.providers.get(this.primaryId) : undefined;
    if (!p) throw new Error('[VaultStorage] No storage provider registered.');
    return p;
  }

  getById(id: string): VaultStorageProvider | undefined {
    return this.providers.get(id);
  }

  list(): VaultStorageProvider[] {
    return Array.from(this.providers.values());
  }

  /**
   * Upload using the primary provider.
   * Falls back to secondary if primary fails (future: write-through).
   */
  async upload(key: string, blob: Blob, options?: StorageUploadOptions): Promise<StorageObject> {
    return this.getPrimary().upload(key, blob, options);
  }

  async download(key: string): Promise<Blob | null> {
    return this.getPrimary().download(key);
  }

  async getUrl(key: string, expiresInSeconds?: number): Promise<string | null> {
    return this.getPrimary().getUrl(key, expiresInSeconds);
  }

  async delete(key: string): Promise<void> {
    return this.getPrimary().delete(key);
  }
}

export const vaultStorage = new _VaultStorageManager();

// ─── Key builder — keeps paths consistent ─────────────────────────────────────

export const VaultStorageKeys = {
  /** Original uploaded file */
  original: (userId: string, itemId: string, ext: string) =>
    `vault/${userId}/originals/${itemId}.${ext}`,

  /** Processed/normalized version */
  processed: (userId: string, itemId: string) =>
    `vault/${userId}/processed/${itemId}.txt`,

  /** Thumbnail image */
  thumbnail: (userId: string, itemId: string) =>
    `vault/${userId}/thumbnails/${itemId}.jpg`,

  /** AI-generated summary */
  aiSummary: (userId: string, itemId: string) =>
    `vault/${userId}/ai/${itemId}-summary.json`,

  /** Vector embedding */
  embedding: (userId: string, itemId: string) =>
    `vault/${userId}/embeddings/${itemId}.json`,
};

// ─── Upload limits ────────────────────────────────────────────────────────────

export const VAULT_STORAGE_LIMITS = {
  /** 50 MB per file */
  maxFileSizeBytes: 50 * 1024 * 1024,
  /** Allowed MIME types for upload */
  allowedMimeTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/markdown',
    'text/html',
    'text/csv',
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'audio/mpeg',
    'audio/mp4',
    'audio/wav',
    'audio/ogg',
    'video/mp4',
    'video/webm',
  ],
};

// ─── Security helpers ─────────────────────────────────────────────────────────

/** Validate a key belongs to the given user (prevents path traversal) */
export function assertKeyOwnership(key: string, userId: string): void {
  if (!key.startsWith(`vault/${userId}/`)) {
    throw new Error(`[VaultStorage] Access denied: key does not belong to user ${userId}`);
  }
}

/** Validate MIME type is in the allowed list */
export function validateMimeType(mimeType: string): boolean {
  return VAULT_STORAGE_LIMITS.allowedMimeTypes.includes(mimeType);
}

/** Validate file size is within limits */
export function validateFileSize(sizeBytes: number): boolean {
  return sizeBytes <= VAULT_STORAGE_LIMITS.maxFileSizeBytes;
}
